import { useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowLeft, ArrowRight, Clock } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { journalPosts } from '../data/journal';
import { Reveal } from '../components/Reveal';

const formatDate = (iso, lang) =>
  new Date(iso).toLocaleDateString(lang === 'id' ? 'id-ID' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' });

const JournalPost = () => {
  const { slug } = useParams();
  const { lang, t } = useLanguage();
  const post = journalPosts.find((p) => p.slug === slug);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);

  useEffect(() => {
    if (post) document.title = `${post.title[lang]} — Yoeli Chan Journey`;
    return () => { document.title = 'Yoeli Chan Journey — Your Journey, Your Way'; };
  }, [post, lang]);

  if (!post) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-ivory px-5" data-testid="journal-not-found">
        <p className="font-heading text-3xl text-navy">{t.destinationsPage.notFound}</p>
        <Link to="/journal" className="mt-6 text-sm uppercase tracking-[0.2em] text-gold-dark" data-testid="back-to-journal">
          {t.journal.back}
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-ivory" data-testid={`journal-post-${post.slug}`}>
      <section ref={heroRef} className="relative flex h-[58vh] min-h-[24rem] items-end overflow-hidden bg-navy">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <img src={post.image} alt={post.title[lang]} className="h-full w-full scale-105 object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/90 via-navy-dark/25 to-navy-dark/30" />
        <div className="relative mx-auto w-full max-w-4xl px-5 pb-14 md:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>
            <Link to="/journal" className="mb-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-ivory/70 transition-colors hover:text-gold" data-testid="back-to-journal">
              <ArrowLeft className="h-4 w-4" /> {t.journal.back}
            </Link>
            <div className="flex flex-wrap items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.25em] text-ivory/60">
              <span className="text-gold">{post.category[lang]}</span>
              <span aria-hidden="true">·</span>
              <span>{formatDate(post.date, lang)}</span>
              <span aria-hidden="true">·</span>
              <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {post.readMinutes} {t.journal.minRead}</span>
            </div>
            <h1 className="mt-4 font-heading text-3xl font-medium leading-tight tracking-tight text-ivory md:text-5xl" data-testid="journal-post-title">
              {post.title[lang]}
            </h1>
          </motion.div>
        </div>
      </section>

      <article className="mx-auto max-w-3xl px-5 py-16 md:py-24" data-testid="journal-post-content">
        {post.content.map((para, i) => (
          <Reveal key={i} delay={Math.min(i * 0.04, 0.15)}>
            <p className={`text-navy/80 leading-relaxed ${i === 0 ? 'font-heading text-xl italic md:text-2xl' : 'mt-8 text-base md:text-lg'}`}>
              {para[lang]}
            </p>
          </Reveal>
        ))}

        <Reveal className="mt-16 border-l-2 border-gold bg-beige p-8 md:p-10">
          <h2 className="font-heading text-2xl text-navy md:text-3xl">{t.journal.ctaTitle}</h2>
          <p className="mt-3 text-sm leading-relaxed text-navy/60">{t.journal.ctaSub}</p>
          <Link
            to="/plan"
            data-testid="journal-cta-button"
            className="group mt-7 inline-flex items-center gap-3 bg-gold px-8 py-3.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-navy transition-colors duration-300 hover:bg-gold-light"
          >
            {t.journal.ctaButton}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
          </Link>
        </Reveal>
      </article>
    </div>
  );
};

export default JournalPost;
